import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import FoodShell from "../../layouts/FoodShell";
import { appModules } from "../../components/global";
import { GET, POST, PUT, Select, alert, loading } from "../../components/CustomComponent";
import { groupOptions, sourceOptions, knownTags, mealSlots } from "./mockData";
import { Panel, KpiStrip, Chip, NutritionLine, TagInput, Skeleton, GROUP_LABELS, SOURCE_LABELS, MEAL_LABELS } from "./FoodUi";

const modalAnimation = !(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
const formDefaults = { uid: "", name: "", emoji: "", kcal: "", protein: "", carbs: "", fat: "", price: "", food_group: "rice", source: "restaurant", tags: [], meals: ["lunch", "dinner"], is_active: true };

// ฐานข้อมูลเมนู: โหลดครั้งเดียว (backend cache) แล้วค้นหา/กรองในเครื่อง เพิ่ม/แก้เมนูได้ ระบบจะเอาไปแนะนำทันที
export default function FoodFoods() {
  const { title, description } = appModules["food_foods"];
  const [foods, setFoods] = useState(null);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("");
  const [source, setSource] = useState("");
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [estimating, setEstimating] = useState(false);

  const load = async () => {
    const result = await GET("v1/food/foods");
    if (result?.status === true) setFoods(result.data.items);
  };

  useEffect(() => {
    load();
  }, []);

  const keyword = search.trim().toLowerCase();
  const list = (foods || []).filter((item) => (!group || item.group === group) && (!source || item.source === source) && (!keyword || item.name.toLowerCase().includes(keyword) || item.tags.some((tag) => tag.toLowerCase().includes(keyword))));
  const avg = (key) => (foods?.length ? Math.round(foods.reduce((total, item) => total + item[key], 0) / foods.length) : 0);

  const openForm = (food) => {
    setDetail(null);
    setForm(food ? { uid: food.uid, name: food.name, emoji: food.emoji || "", kcal: food.kcal, protein: food.protein, carbs: food.carbs, fat: food.fat, price: food.price, food_group: food.group, source: food.source, tags: food.tags, meals: food.meals, is_active: food.is_active } : { ...formDefaults });
  };

  // ให้ AI ประเมินจากชื่อในฟอร์ม แล้วเติมตัวเลข/ประเภท/แท็กให้ (ผู้ใช้แก้ก่อนบันทึกได้)
  const estimateWithAi = async () => {
    const name = form.name.trim();
    if (!name) {
      await alert("", "warning", "แจ้งเตือน", "พิมพ์ชื่อเมนูก่อน แล้วให้ AI ประเมินให้");
      return;
    }
    setEstimating(true);
    const result = await POST("v1/food/foods/estimate", { name });
    setEstimating(false);
    if (result?.status !== true) return;
    const { estimate, similar } = result.data;
    if (similar && !(await alert("", "info", "มีเมนูคล้ายกันอยู่แล้ว", `"${similar.name}" (${similar.kcal} kcal) มีในฐานแล้ว ยังต้องการเติมค่าจาก AI ต่อหรือไม่?`, "เติมต่อ", "ยกเลิก"))) return;
    setForm({ ...form, name: estimate.name || form.name, emoji: estimate.emoji || form.emoji, kcal: estimate.kcal, protein: estimate.protein, carbs: estimate.carbs, fat: estimate.fat, price: estimate.price, food_group: estimate.food_group, source: estimate.source, tags: estimate.tags || [], meals: estimate.meals || [], ai_note: estimate.note });
  };

  const saveForm = async () => {
    if (!form.name.trim() || form.kcal === "") {
      await alert("", "warning", "แจ้งเตือน", "กรุณากรอกชื่อเมนูและพลังงาน (kcal)");
      return;
    }
    const body = {
      name: form.name.trim(),
      emoji: form.emoji,
      kcal: Number(form.kcal) || 0,
      protein: Number(form.protein) || 0,
      carbs: Number(form.carbs) || 0,
      fat: Number(form.fat) || 0,
      price: Number(form.price) || 0,
      food_group: form.food_group,
      source: form.source,
      tags: form.tags,
      meals: form.meals,
      is_active: !!form.is_active,
    };
    loading(true);
    const result = form.uid ? await PUT(`v1/food/foods/${form.uid}`, body, null, { keepEmpty: true }) : await POST("v1/food/foods", body, null, { keepEmpty: true });
    loading(false);
    if (result?.status === true) {
      setForm(null);
      alert("", "success", "สำเร็จ", form.uid ? "แก้ไขเมนูแล้ว" : "เพิ่มเมนูแล้ว ระบบจะนำไปใช้แนะนำทันที");
      load();
    }
  };

  const toggleMeal = (key) => setForm({ ...form, meals: form.meals.includes(key) ? form.meals.filter((item) => item !== key) : [...form.meals, key] });
  const toggleTag = (tag) => setForm({ ...form, tags: form.tags.includes(tag) ? form.tags.filter((item) => item !== tag) : [...form.tags, tag] });

  return (
    <FoodShell title={title} description={description}>
      <Panel
        eyebrow="Food database"
        icon="fa-solid fa-book"
        title="ฐานข้อมูลเมนู"
        desc="เมนูที่ AI ใช้แนะนำ พลังงานและสารอาหารเป็นค่าประมาณต่อ 1 จาน เพิ่มเมนูที่กินประจำได้"
        actions={<button type="button" className="btn stg-btn stg-btn--primary" onClick={() => openForm(null)}><i className="fa-solid fa-plus" />เพิ่มเมนู</button>}
      >
        <KpiStrip
          className="mb-3"
          items={[
            ["orange", "fa-solid fa-utensils", foods?.length || 0, "เมนูทั้งหมด", "จำนวนเมนูทั้งหมดในฐานข้อมูล"],
            ["red", "fa-solid fa-fire", avg("kcal"), "พลังงานเฉลี่ย (kcal/จาน)", "ค่าเฉลี่ยพลังงานต่อ 1 จานของทุกเมนู"],
            ["green", "fa-solid fa-drumstick-bite", avg("protein"), "โปรตีนเฉลี่ย (g/จาน)", "ค่าเฉลี่ยโปรตีนต่อ 1 จานของทุกเมนู"],
            ["gold", "fa-solid fa-coins", `฿${avg("price").toLocaleString()}`, "ราคาเฉลี่ยต่อจาน", "ค่าเฉลี่ยราคาต่อ 1 จานของทุกเมนู"],
          ]}
        />
        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <div className="stg-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input type="text" className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="ค้นหาชื่อเมนูหรือแท็ก..." />
            </div>
          </div>
          <div className="col-md-3"><Select className="form-control" value={group} options={groupOptions} placeholder="ทุกประเภท" onChange={setGroup} /></div>
          <div className="col-md-3"><Select className="form-control" value={source} options={sourceOptions} placeholder="ทุกแหล่งที่มา" onChange={setSource} /></div>
        </div>

        <div className="table-responsive stg-table-wrap">
          <table className="table table-hover align-middle stg-table stg-table--compact food-table">
            <thead>
              <tr>
                <th>เมนู</th>
                <th className="text-center">ประเภท</th>
                <th className="text-end">kcal</th>
                <th className="text-end">โปรตีน</th>
                <th className="text-end">คาร์บ</th>
                <th className="text-end">ไขมัน</th>
                <th className="text-end">ราคา</th>
                <th>แท็ก</th>
                <th className="text-center">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {!foods ? (
                <tr><td colSpan="9"><Skeleton lines={5} /></td></tr>
              ) : list.length ? list.map((item) => (
                <tr key={item.uid} className={`food-row-link ${item.is_active ? "" : "opacity-50"}`} onClick={() => setDetail(item)}>
                  <td>
                    <span className="d-flex align-items-center gap-2">
                      <span className="food-card-emoji food-card-emoji--sm">{item.emoji || "🍽️"}</span>
                      <span>
                        <span className="stg-cell-title d-block">{item.name}{!item.is_active ? <Chip tone="muted" className="ms-2">ปิดใช้</Chip> : null}</span>
                        <span className="stg-cell-sub">{SOURCE_LABELS[item.source]}{item.origin === "ai" ? " · AI ประเมิน" : item.is_seed ? "" : " · เพิ่มเอง"}</span>
                      </span>
                    </span>
                  </td>
                  <td className="text-center text-nowrap"><Chip tone="muted">{GROUP_LABELS[item.group]}</Chip></td>
                  <td className="text-end fw-semibold">{item.kcal}</td>
                  <td className="text-end">{item.protein} g</td>
                  <td className="text-end">{item.carbs} g</td>
                  <td className="text-end">{item.fat} g</td>
                  <td className="text-end fw-semibold">฿{item.price}</td>
                  <td><span className="d-flex flex-wrap gap-1">{item.tags.map((tag) => <Chip key={tag} tone="orange">{tag}</Chip>)}</span></td>
                  <td className="text-center" onClick={(event) => event.stopPropagation()}>
                    <div className="food-row-actions">
                      <button type="button" className="stg-icon-btn" title="ดูรายละเอียด" onClick={() => setDetail(item)}><i className="fa-solid fa-eye" /></button>
                      <button type="button" className="stg-icon-btn" title="แก้ไข" onClick={() => openForm(item)}><i className="fa-solid fa-pen" /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="9" className="stg-empty"><i className="fa-regular fa-folder-open" />ไม่พบเมนูที่ค้นหา</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {foods ? <p className="food-table-foot">แสดง <strong>{list.length}</strong> จาก {foods.length} เมนู · คลิกที่แถวเพื่อดูรายละเอียด</p> : null}
      </Panel>

      {/* รายละเอียดเมนู */}
      <Modal animation={modalAnimation} show={!!detail} onHide={() => setDetail(null)} centered dialogClassName="stg-modal food-modal food-modal--detail">
        {detail ? (
          <>
            <Modal.Header closeButton>
              <div>
                <p className="stg-eyebrow">{GROUP_LABELS[detail.group]} · {SOURCE_LABELS[detail.source]}{detail.origin === "ai" ? " · AI ประเมิน" : ""}</p>
                <Modal.Title>{detail.emoji} {detail.name}</Modal.Title>
                <p className="stg-modal-sub">ค่าประมาณต่อ 1 จาน</p>
              </div>
            </Modal.Header>
            <Modal.Body>
              <KpiStrip
                className="mb-3"
                items={[
                  ["orange", "fa-solid fa-fire", detail.kcal, "kcal"],
                  ["red", "fa-solid fa-drumstick-bite", `${detail.protein} g`, "โปรตีน"],
                  ["gold", "fa-solid fa-bowl-rice", `${detail.carbs} g`, "คาร์บ"],
                  ["green", "fa-solid fa-seedling", `${detail.fat} g`, "ไขมัน"],
                ]}
              />
              <div className="food-detail-row"><span>ราคาประมาณ</span><strong>฿{detail.price}</strong></div>
              <div className="food-detail-row"><span>เหมาะกับมื้อ</span><span className="d-flex flex-wrap gap-1 justify-content-end">{detail.meals.map((meal) => <Chip key={meal} tone="muted">{MEAL_LABELS[meal]}</Chip>)}</span></div>
              <div className="food-detail-row"><span>แท็ก</span><span className="d-flex flex-wrap gap-1 justify-content-end">{detail.tags.map((tag) => <Chip key={tag} tone="orange">{tag}</Chip>)}</span></div>
              <div className="food-detail-row"><span>สารอาหาร</span><NutritionLine food={detail} showPrice={false} /></div>
              {detail.ai_note ? <div className="food-detail-row"><span>หมายเหตุจาก AI</span><span className="text-end">{detail.ai_note}</span></div> : null}
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setDetail(null)}>ปิด</button>
              <button type="button" className="btn stg-btn stg-btn--primary" onClick={() => openForm(detail)}><i className="fa-solid fa-pen" />แก้ไขเมนู</button>
            </Modal.Footer>
          </>
        ) : null}
      </Modal>

      {/* เพิ่ม/แก้ไขเมนู */}
      <Modal animation={modalAnimation} show={!!form} onHide={() => setForm(null)} centered size="lg" dialogClassName="stg-modal food-modal modal-dialog-scrollable">
        {form ? (
          <>
            <Modal.Header closeButton>
              <div>
                <p className="stg-eyebrow">Food database</p>
                <Modal.Title>{form.uid ? "แก้ไขเมนู" : "เพิ่มเมนูใหม่"}</Modal.Title>
                <p className="stg-modal-sub">ใส่ค่าประมาณต่อ 1 จาน แท็กมาตรฐานมีผลกับการแนะนำ (โหมด/ช่วงเวลา)</p>
              </div>
            </Modal.Header>
            <Modal.Body>
              <div className="row g-3">
                <div className="col-md-2"><label className="stg-label">Emoji</label><input type="text" className="form-control text-center" value={form.emoji} maxLength={4} placeholder="🍛" onChange={(event) => setForm({ ...form, emoji: event.target.value })} /></div>
                <div className="col-md-10">
                  <label className="stg-label">ชื่อเมนู *</label>
                  <div className="d-flex gap-2">
                    <input type="text" className="form-control" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="เช่น ข้าวกะเพราไก่ + ไข่ดาว" onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); estimateWithAi(); } }} />
                    <button type="button" className="btn stg-btn stg-btn--ghost text-nowrap" onClick={estimateWithAi} disabled={estimating} title="ให้ AI ประเมิน kcal / สารอาหาร / ราคา / แท็ก จากชื่อเมนู">
                      <i className={estimating ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-wand-magic-sparkles"} />ให้ AI ประเมิน
                    </button>
                  </div>
                  {form.ai_note ? <div className="stg-help"><i className="fa-solid fa-robot me-1" />{form.ai_note} (ค่าประมาณ แก้ได้ก่อนบันทึก)</div> : null}
                </div>
                <div className="col-6 col-md-3"><label className="stg-label">kcal *</label><input type="number" className="form-control" value={form.kcal} onChange={(event) => setForm({ ...form, kcal: event.target.value })} /></div>
                <div className="col-6 col-md-3"><label className="stg-label">โปรตีน (g)</label><input type="number" className="form-control" value={form.protein} onChange={(event) => setForm({ ...form, protein: event.target.value })} /></div>
                <div className="col-6 col-md-3"><label className="stg-label">คาร์บ (g)</label><input type="number" className="form-control" value={form.carbs} onChange={(event) => setForm({ ...form, carbs: event.target.value })} /></div>
                <div className="col-6 col-md-3"><label className="stg-label">ไขมัน (g)</label><input type="number" className="form-control" value={form.fat} onChange={(event) => setForm({ ...form, fat: event.target.value })} /></div>
                <div className="col-md-4"><label className="stg-label">ราคาประมาณ (บาท)</label><input type="number" className="form-control" value={form.price} onChange={(event) => setForm({ ...form, price: event.target.value })} /></div>
                <div className="col-md-4"><label className="stg-label">ประเภท</label><Select className="form-control" value={form.food_group} options={groupOptions} onChange={(value) => setForm({ ...form, food_group: value })} /></div>
                <div className="col-md-4"><label className="stg-label">แหล่งที่มา</label><Select className="form-control" value={form.source} options={sourceOptions} onChange={(value) => setForm({ ...form, source: value })} /></div>
                <div className="col-12">
                  <label className="stg-label">เหมาะกับมื้อ</label>
                  <div className="d-flex flex-wrap gap-1">
                    {mealSlots.map((slot) => <Chip key={slot.key} tone="orange" icon={slot.icon} active={form.meals.includes(slot.key)} onClick={() => toggleMeal(slot.key)}>{slot.label}</Chip>)}
                  </div>
                </div>
                <div className="col-12">
                  <label className="stg-label">แท็กมาตรฐาน (กดเลือก)</label>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {knownTags.map((tag) => <Chip key={tag} tone="muted" active={form.tags.includes(tag)} onClick={() => toggleTag(tag)}>{tag}</Chip>)}
                  </div>
                  <label className="stg-label">แท็กทั้งหมด (พิมพ์เพิ่มได้)</label>
                  <TagInput value={form.tags} onChange={(tags) => setForm({ ...form, tags })} placeholder="พิมพ์แล้ว Enter" />
                </div>
                {form.uid ? (
                  <div className="col-12">
                    <Chip tone={form.is_active ? "green" : "muted"} icon={form.is_active ? "fa-solid fa-check" : "fa-regular fa-circle"} onClick={() => setForm({ ...form, is_active: !form.is_active })}>{form.is_active ? "เปิดใช้ (ระบบแนะนำเมนูนี้ได้)" : "ปิดใช้ (ซ่อนจากการแนะนำ)"}</Chip>
                  </div>
                ) : null}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setForm(null)}>ยกเลิก</button>
              <button type="button" className="btn stg-btn stg-btn--primary" onClick={saveForm}><i className="fa-solid fa-floppy-disk" />บันทึกเมนู</button>
            </Modal.Footer>
          </>
        ) : null}
      </Modal>
    </FoodShell>
  );
}
